import { useMutation } from '@apollo/client'
import { useCallback, useContext } from 'react'

import {
    ShowGlobalSnackbarMutation,
    ShowGlobalSnackbarMutationProps,
    ShowGlobalSnackbarRequest,
} from '@deltek/specification-client/core/Configurations/GlobalSnackbar'
import { ProjectElementNode } from '@deltek/specification-client/core/Models/productListingDetail'
import {
    ProjectElementMetadataInput,
    UpdateElementInput,
} from '@deltek/specification-client/core/Models/projectElementInput'
import {
    UpdateProjectNuggetElementMutation,
    UpdateProjectProductFamilyElementMutation,
    UpdateProjectUniformatClassificationElementMutation,
} from 'api/graphQL/mutations/projectElement.mutations'
import {
    UpdateProjectElementPropertiesResponse,
    UpdateProjectNuggetElementResponse,
    UpdateProjectProductFamilyElementResponse,
} from 'api/graphQL/types/projectElement.types'
import { WorkspaceContentContext } from 'pages/ProjectsPage/WorkspacePage/components/WorkspaceContentContext'

/**
 * Interface representing the properties required for Project Data API.
 */
export interface ProjectPropertiesApiProps {
    projectId: string
    contentTeamFromStudioPage: boolean
}

/**
 * A custom hook that provides functionality to fetch and manage project data.
 *
 * @template T - The type of the props that extend `ProjectDataApiProps`.
 * @param {T} props - The properties required to initialize the hook.
 * @param {string} props.projectId - The ID of the project to fetch data for.
 * @returns {Object} An object containing the `fetchProjectData` function.
 * @returns {Function} returns.fetchProjectData - A function to fetch the project data.
 */
export const useProjectPropertiesMutation = <
    T extends ProjectPropertiesApiProps
>({
    projectId,
    contentTeamFromStudioPage,
}: T) => {
    const { handleRetrieveUpdatedDtvPerPartType } = useContext(
        WorkspaceContentContext
    )

    const [showGlobalSnackbar] = useMutation<
        ShowGlobalSnackbarRequest,
        ShowGlobalSnackbarMutationProps
    >(ShowGlobalSnackbarMutation)

    const [updatePUCElementProperties] = useMutation<
        UpdateProjectElementPropertiesResponse,
        { input: UpdateElementInput }
    >(UpdateProjectUniformatClassificationElementMutation, {
        onError: (error) => {
            showGlobalSnackbar({
                variables: {
                    message: error.message ?? '',
                    variant: 'error',
                    id: 'ProjectWorkspaceContentArea_PUCElement',
                },
            })
        },
    })

    const [updateProjectProductFamilyElement] = useMutation<
        UpdateProjectProductFamilyElementResponse,
        { input: UpdateElementInput }
    >(UpdateProjectProductFamilyElementMutation, {
        onCompleted: () => {
            // When the target LOS of family element is updated, the DTVs in all the tabs/part types should be updated
            // to reflect the new contents visibility based on the updated target LOS.
            // Note: Had to call repeated queries as a workaround to update the DTVs in all the tabs/part types.
            // This is due to Apollo's behavior issue where multiple queries use same underlying data.
            // This will be fixed if we upgrade in the new version of Apollo.
            if (contentTeamFromStudioPage) {
                handleRetrieveUpdatedDtvPerPartType?.('ALL')
            } else {
                handleRetrieveUpdatedDtvPerPartType?.('GENERAL')
                handleRetrieveUpdatedDtvPerPartType?.('PRODUCTS')
                handleRetrieveUpdatedDtvPerPartType?.('EXECUTION')
            }
        },
        onError: (error) => {
            showGlobalSnackbar({
                variables: {
                    message: error.message ?? '',
                    variant: 'error',
                    id:
                        'ProjectWorkspaceContentArea_ProjectProductFamilyElement',
                },
            })
        },
    })

    const [updateProjectNuggetElement] = useMutation<
        UpdateProjectNuggetElementResponse,
        { input: UpdateElementInput }
    >(UpdateProjectNuggetElementMutation, {
        onError: (error) => {
            showGlobalSnackbar({
                variables: {
                    message: error.message ?? '',
                    variant: 'error',
                    id: 'ProjectWorkspaceContentArea_ProjectNuggetElement',
                },
            })
        },
    })

    const updateProjectProperties = useCallback(
        (
            finalSelectedValues: ProjectElementMetadataInput,
            projectElementNode: ProjectElementNode | null
        ) => {
            const inputValues = {
                variables: {
                    input: {
                        projectElementMetadataInput: finalSelectedValues,
                        elementId: projectElementNode?.elementId ?? '',
                        projectId: projectId ?? '',
                    },
                },
            }

            projectElementNode?.elementType === 'ASSEMBLY'
                ? updatePUCElementProperties(inputValues)
                : projectElementNode?.elementType === 'PRODUCTFAMILY'
                ? updateProjectProductFamilyElement(inputValues)
                : updateProjectNuggetElement(inputValues)
        },
        [
            projectId,
            updateProjectNuggetElement,
            updateProjectProductFamilyElement,
            updatePUCElementProperties,
        ]
    )
    return { updateProjectProperties }
}
